import { cn } from "@/lib/utils";
import Image from "next/image";
import { Skeleton } from "@/components/ui/skeleton";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import PlayButton from "./play-button";
import ExpandButton from "./expand-button";
import { GalleryItem } from "../types/common";
import { PlaceholderImage } from "@/components/common/placeholder-image";
import { useId } from "react";

type CardProps = {
  item?: GalleryItem;
  onClick?: (d: number | null) => void;
  loading?: boolean;
};

const CardSkeleton = () => {
  return (
    <>
      <Skeleton className="h-full w-full rounded-[max(2.083vw,30px)] bg-primary/5" />
    </>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CardContent = ({ item, onClick }: any) => {
  const isImage = !item.video_link && !!item.image_link;
  return (
    <>
      {item?.image_link ? (
        <Image
          src={item?.image_link}
          fill
          sizes="400px"
          alt="thumbnail" // since no consistent title comaxg from backend
          objectFit="cover"
          className="rounded-[max(2.083vw,30px)]"
        />
      ) : (
        <PlaceholderImage />
      )}
      <div className={cn("absolute inset-0 z-[20] rounded-[max(2.083vw,30px)] bg-black/40")} />
      <div
        className={cn(
          "z-[90] flex items-center justify-center opacity-70",
          "absolute bottom-2 left-2 lg:left-[50%] lg:top-[50%] lg:translate-x-[-50%] lg:translate-y-[-50%]"
        )}
      >
        {isImage ? <ExpandButton onClick={onClick} /> : <PlayButton onClick={onClick} />}
      </div>
    </>
  );
};

export default function Card({ item, onClick, loading }: CardProps) {
  const cardGradientId = useId();
  return (
    <div className="relative select-none">
      <svg
        className="pointer-events-none absolute inset-0 z-10 h-full w-full"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
      >
        <defs>
          <linearGradient
            id={cardGradientId}
            x1="840"
            y1="0"
            x2="840"
            y2="436"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#F0DFC1" />
            <stop offset="0.791452" stopColor="#C89452" />
            <stop offset="1" stopColor="#B07F44" />
          </linearGradient>
        </defs>
        <rect
          x="0.5"
          y="0.5"
          width="calc(100% - 1px)"
          height="calc(100% - 1px)"
          rx="max(2.083vw, 30px)"
          ry="max(2.083vw, 30px)"
          fill="none"
          stroke={`url(#${cardGradientId})`}
          strokeWidth="1"
        />
      </svg>
      <div className="flex flex-col gap-6 rounded-[max(2.083vw,30px)] bg-[rgba(58,40,92,0.22)] px-4 pb-4 pt-4 backdrop-blur-[17px]">
        <div className="relative">
          <AspectRatio ratio={16 / 9}>
            <div className="relative h-full w-full rounded-[max(2.083vw,30px)]">
              {loading ? <CardSkeleton /> : <CardContent item={item} onClick={onClick} />}
            </div>
          </AspectRatio>
        </div>
      </div>
    </div>
  );
}
